import { OptionBase, OptionFull } from '../components/MainPage/_shared/Options';
import React from 'react';
import { Option } from './catalog/OptionsPage/OptionsTable';
import { ProductReceived } from './catalog/ProductPage/types';
import { IngredientReceived } from './catalog/IngredientPage/types';

const sortByOptions = (
  a: OptionFull[],
  b: OptionFull[],
  getValue: (options: OptionFull[]) => string | null
) => {
  return getValue(a)!.localeCompare(getValue(b)!, 'ru');
};

export const getOptionsName = (options: OptionFull[], skipText = false) => {
  if (!options.length) return skipText ? null : 'Без опций';
  return options
    .sort((a, b) => a.position - b.position)
    .map(({ valueName }) => valueName)
    .join(', ');
};

const getOptionsPositions = (options: OptionFull[]) => {
  if (!options.length) return String(0);
  return options
    .map(
      ({ position, valuePosition }) => String(position) + String(valuePosition)
    )
    .sort()
    .join(', ');
};

export const sortByOptionsPositions = (
  a: { options?: OptionFull[] },
  b: { options?: OptionFull[] }
) => {
  if (!a.options || !b.options) return 0;
  return sortByOptions(a.options, b.options, getOptionsPositions);
};

export const reduceVariants = <T extends { options: OptionFull[] }>(
  variants: T[]
) =>
  variants.map((variant) =>
    variant.options.reduce<Record<string, string>>(
      (acc, { name, valueName }) => ({ ...acc, [name]: valueName }),
      {}
    )
  );

export const preventNegative = (e: React.KeyboardEvent<HTMLInputElement>) => {
  e.key === '-' && e.preventDefault();
};
export const preventLeadingZero = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.value.match(/^0\d+/)) {
    e.target.value = e.target.value.replace(/^0+/, '');
  }
};

export const preventNegativeAndDecimal = (
  e: React.KeyboardEvent<HTMLInputElement>
) =>
  (e.key === 'e' ||
    e.key === '+' ||
    e.key === '-' ||
    e.key === '.' ||
    e.key === ',') &&
  e.preventDefault();

export const preventEnterOnSubmit = (e: React.KeyboardEvent<HTMLElement>) =>
  e.key === 'Enter' && e.preventDefault();

export const generateOptionValueIdToIsActive = (
  cachedOptions: Option[]
): Map<number, boolean> => {
  return (
    cachedOptions.reduce((acc: Map<number, boolean>, option) => {
      return option.values.reduce((mapAcc, value) => {
        mapAcc.set(value.id, value.isActive);
        return mapAcc;
      }, acc);
    }, new Map<number, boolean>()) || new Map<number, boolean>()
  );
};

export const hasInactiveOptionValue = (
  options: OptionBase[],
  optionValueIdToIsActive: Map<number, boolean>
): boolean => {
  return options.some(({ valueId }) => !optionValueIdToIsActive.get(valueId));
};

export const checkAllVariantsActive = (
  variants: ProductReceived['variants'] | IngredientReceived['variants'],
  optionValueIdToIsActive: Map<number, boolean>
) =>
  variants.every(({ options, isActive }) => {
    const variantHasInactiveOption = hasInactiveOptionValue(
      options,
      optionValueIdToIsActive
    );
    return variantHasInactiveOption || isActive;
  });

type Receivable = ProductReceived | IngredientReceived;

export const getToggledVariants = <T extends Receivable>(
  values: T,
  optionValueIdToIsActive: Map<number, boolean>,
  areAllVariantsActive: boolean
): T => {
  return {
    ...values,
    variants: values.variants.map((variant) => {
      const hasVariantInactiveOptionValue = hasInactiveOptionValue(
        variant.options,
        optionValueIdToIsActive
      );
      if (hasVariantInactiveOptionValue) return variant;
      return {
        ...variant,
        isActive: !areAllVariantsActive,
      };
    }),
  };
};
